import React, { useEffect, useState } from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMMenuBar } from "../../components/Complex/CRMMenuBar/CRMMenuBar";
import { CRMColors } from "../../models/CRMColors";
import { CRMKPIs } from "../../components/Simple/CRMKPIs/CRMKPIs";
import { CRMHomesSearchOverlay } from "../../components/Complex/CRMCaseSearchOverlay/CRMHomesSearchOverlay";
import { createChangeRouteAction } from "../../state/router/createChangeRouteAction";
import * as Party2 from "../../../../domain/models/Party2";
import * as Listing3 from "../../../../domain/models/Listing3";
import { CRMBlockFilterCheckboxes } from "../../components/Simple/CRMBlockFilterCheckboxes/CRMBlockFilterCheckboxes";
import { FeatureToggle } from "../../../../domain/codecs/FeatureToggle";
import { ZIndex } from "../../components/BuildingBlocks/ZIndex";
import { CRMZIndex } from "../../models/CRMZIndex";
import { isLocal, isStaging } from "../../functions/featureFlag";

export const CRMHomesMenuBarContainer = (props: TContainerStateProps): JSX.Element => {
    const [kpisIsOpen, setKPISIsOpen] = useState<boolean>(false);
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);

    const search = props.state.activeData.crm.search;

    const onSearchChange = (searchInput: string) => props.dispatch({
        type: "CRM_SEARCH_INPUT_CHANGED",
        payload: searchInput,
    });

    const closeSearch = () => {
        setIsSearchOpen(false);
        onSearchChange("");
    };

    // Ctrl+Space, the same shortcut Sail Legal uses. It lives on the menu bar rather than
    // the search page so the overlay opens from anywhere in the CRM, which is what makes
    // the nav icon a toggle rather than a link to one particular page.
    useEffect(
        () => {
            const listen = (event: KeyboardEvent) => {
                if (event.ctrlKey === true && event.code === "Space") {
                    setIsSearchOpen((open) => !open);
                }
            };
            window.addEventListener("keydown", listen);
            return () => window.removeEventListener("keydown", listen);
        },
        []
    );

    
    const isSessionUserAdmin = (): boolean => props.state.global.user.user_role === "admin";

    return (
        <>
            <CRMMenuBar
                logoHref="/crm/search"
                colors={{
                    backgroundColor: CRMColors.PRIMARY_0,
                    logoBackgroundColor: CRMColors.PRIMARY_2,
                    logoIconColor: "primary-12",
                    hoverColor: CRMColors.PRIMARY_4,
                }}
                leftButtons={[
                    {
                        icon: "house-with-person",
                        label: "Make Enquiry",
                        href: "/crm/add-enquiry",
                    },
                    {
                        icon: "person-add",
                        label: "Add Party",
                        onClick: () => props.dispatch({type: "CRM_ADD_NEW_PARTY_BUTTON_CLICKED"})
                    },
                    {
                        icon: "house-and-building",
                        label: "View Listings",
                        href: "/crm/listings",
                    },
                    {
                        icon: "money",
                        label: "Profit report",
                        href: "/crm/listings/profit-report",
                    },
                    {
                        icon: "search-list",
                        label: "Search",
                        onClick: () => setIsSearchOpen((open) => !open),
                    },
                ]}
                rightButtons={[
                    {
                        icon: "book",
                        label: "Guides",
                        href: "/crm/guides/homes",
                    },
                    {
                        icon: "speed",
                        label: "KPIs",
                        onClick: () => setKPISIsOpen(true)
                    },
                    // Mirrors the legal menu bar: switchable in staging and local only,
                    // never in production. The Sail Homes CRM had no way to reach these
                    // at all, so a toggle gating a Sail Homes page could only be flipped
                    // from the other brand's portal — where the session is different.
                    ...(
                        isStaging
                        || isLocal
                            ? [<ZIndex zIndex={CRMZIndex.SECOND_Z}>
                                <CRMBlockFilterCheckboxes
                                    heading="Feature Toggles"
                                    backgroundColour="transparent"
                                    popoutPosition="bottom"
                                    icon="flag"
                                    displayBadgeCount={true}
                                    iconColour={CRMColors.NEUTRAL_PAPER}
                                    borderLeftColour="none"
                                    options={FeatureToggle.values}
                                    optionsSelected={props.state.forms.session_feature_toggle_form.edited.enabled_feature_toggles}
                                    onSelectedChange={(enabled_feature_toggles) => props.dispatch({
                                        type: "CRM_SESSION_FEATURE_TOGGLE_CHANGE",
                                        payload: {
                                            ...props.state.forms.session_feature_toggle_form,
                                            edited: {
                                                enabled_feature_toggles,
                                            }
                                        }
                                    })}
                                />
                            </ZIndex>]
                            : []
                    ),
                    {
                        icon: "settings",
                        label: "Settings",
                        href: "/crm/admin",
                        isDisabled: !isSessionUserAdmin()
                    },
                    {
                        // NOT admin-gated: this is every staff member's only route to enrolment.
                        icon: "lock",
                        label: "Security",
                        href: "/auth/authenticator",
                    },
                    {
                        icon: "logout",
                        label: "Logout",
                        href: `${env.REACT_APP_AUTH_URL}/web/logout`,
                        onClick: (e) => {
                            if (confirm("Confirm logout") !== true) {
                                e.preventDefault();
                            }
                        }
                    },
                ]}
            />

            {/* SEARCH OVERLAY */}
            <CRMHomesSearchOverlay
                isOpen={isSearchOpen}
                searchValue={search.searchInput}
                listings={search.listingsSearchResponse.response.data}
                listingsMeta={search.listingsSearchResponse.response.meta}
                parties={search.partiesSearchResponse.response.data}
                partiesMeta={search.partiesSearchResponse.response.meta}
                onChange={onSearchChange}
                onClose={closeSearch}
                onLoadMoreListings={() => props.dispatch({ type: "CRM_SEARCH_LOAD_MORE_LISTINGS" })}
                onLoadMoreParties={() => props.dispatch({ type: "CRM_SEARCH_LOAD_MORE_PARTIES" })}
                onListingClick={(listing: Listing3.T) => props.dispatch(createChangeRouteAction(
                    "VIEW_CRM_LISTING",
                    { listingId: listing.id },
                    { visible_tab: "", visible_sections: "" },
                ))}
                onSellerClick={(listing: Listing3.T) => props.dispatch(createChangeRouteAction(
                    "VIEW_CRM_LISTING",
                    { listingId: listing.id },
                    { visible_tab: "seller_details", visible_sections: "" },
                ))}
                onPartyClick={(party: Party2.T) => props.dispatch(createChangeRouteAction(
                    "VIEW_CRM_PARTY",
                    { partyId: party.id },
                    {},
                ))}
            />

            {/* KPIS */}
            <CRMKPIs
                isOpen={kpisIsOpen}
                onClose={() => setKPISIsOpen(false)}
            />
        </>
    );
};
